import React from 'react';
import _ from 'lodash';

import {
  FormModal,
  LabelledText,
  routeDirectory
} from 'modules/layer00';
import {
  Button,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
  styled
} from '@mui/material';
import {
  errorUtil,
  fields,
  formUtil
} from 'iron-forge-react-modules';

import { selectFields } from 'modules/layer01';
import { useForm } from 'react-hook-form';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();

const HeaderTypography = styled(Typography)({
  fontSize: 20,
  color: '#616264'
});



export interface IProps {
  returnAuthorizationId: string;
  storeId: string;

  onClose: () => void;
  onFinish: () => void;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.returnAuthorizationId);
  const [
    triageTier1ReturnAuthorization,
    triageTier1ReturnAuthorizationTuple
  ] = dataService.useTriageTier1ReturnAuthorization(props.returnAuthorizationId);
  const { genController } = selectFields.TriageReasonSelectField.genComponentAndControllerGenerator(props.storeId);
  const ControlledTriageReasonSelectField = genController<types.IFormState>();

  const form = useForm<types.IFormState>({
    defaultValues: {
      observedIssue: null,
      serialNumber: ''
    }
  });
  formUtil.useSetFormFields(queryTuple, form.setValue, ['serialNumber']);

  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      errorUtil.errorWrap(async () => {
        await triageTier1ReturnAuthorization(formState);
        props.onFinish();
      }, {
        COULD_NOT_CREATE_INVENTORY_ADJUSTMENT: "An inventory adjustment was unable to be created for this triage observation. Triage was not started."
      });
    },
    [props, triageTier1ReturnAuthorization]
  );



  const loading = queryTuple.loading || triageTier1ReturnAuthorizationTuple.loading;

  return (
    <FormModal.FC
      title="Triage Returned Product"
      isDoubleWide
      onClose={props.onClose}
      onSubmit={form.handleSubmit(processSubmit)}
      isLoading={loading}
      fnGenElCloseButton={args => (
        <Button
          {...args}
          variant="outlined"
          color="info"
          onClick={props.onClose}>
          Cancel
        </Button>
      )}
      fnGenElAcceptButton={args => (
        <Button
          {...args}
          variant="contained"
          type="submit">
          Finish
        </Button>
      )}>
      <Stack
        spacing={1.5}
        sx={{ paddingBottom: 1.5 }}>
        <HeaderTypography>
          General RMA Information
        </HeaderTypography>

        <Divider />

        <LabelledText.FC
          direction="horizontal"
          title="Reference Number"
          text={queryTuple.data.returnAuthorization?.referenceNumber} />

        <LabelledText.FC
          direction="horizontal"
          title="RMA Number"
          text={(
            <Link
              target="_blank"
              href={routeDirectory.admin.tier1ReturnAuthorizations.detail.path(props.returnAuthorizationId)}>
              {queryTuple.data.returnAuthorization?.returnNumber}
            </Link>
          )} />

        <LabelledText.FC
          direction="horizontal"
          title="IMEI"
          text={queryTuple.data.returnAuthorization?.serialNumber} />
      </Stack>

      <Stack
        spacing={1.5}
        sx={{
          paddingTop: 1.5,
          paddingBottom: 1.5
        }}>
        <HeaderTypography>
          Reason for return
        </HeaderTypography>

        <Divider />

        <Grid container>
          <Grid
            item
            xs={4}>
            {_.map(queryTuple.data.returnAuthorization?.problemStatements ?? [], (stmt, index) => (
              <LabelledText.FC
                key={index}
                title={`Problem ${index + 1}`}
                text={stmt} />
            ))}
          </Grid>

          <Grid
            item
            xs={8}>
            <LabelledText.FC
              title="Customer Comments"
              text={queryTuple.data.returnAuthorization?.customerComment} />
          </Grid>
        </Grid>
      </Stack>

      <Stack spacing={1.5}>
        <HeaderTypography>
          Device Information
        </HeaderTypography>

        <Divider />

        <Grid
          container
          rowSpacing={1}>
          <Grid
            item
            xs={5}>
            <Typography sx={{
              fontSize: 20,
              fontWeight: 400
            }}>
              Model
            </Typography>
          </Grid>

          <Grid
            item
            xs={7}>
            <Typography>
              {queryTuple.data.returnAuthorization?.inventoryItemName}
            </Typography>
          </Grid>

          <Grid
            item
            xs={5}>
            <Typography sx={{
              fontSize: 20,
              fontWeight: 400
            }}>
              Inbound SKU
            </Typography>
          </Grid>

          <Grid
            item
            xs={7}>
            <Typography>
              {queryTuple.data.returnAuthorization?.inventoryItemSku}
            </Typography>
          </Grid>
        </Grid>

        <Stack spacing={4}>
          <Divider sx={{
            marginTop: 3,
            marginBottom: 1
          }} />

          <ControlledTextField
            isRequired
            isDisabled
            label="IMEI/ICCID"
            fieldKey="serialNumber"
            formControl={form.control} />

          <ControlledTriageReasonSelectField
            isRequired
            isDisabled={loading}
            formControl={form.control}
            fieldKey="observedIssue"
            disableDefaultMargins
            label="Observed Issue" />
        </Stack>
      </Stack>
    </FormModal.FC>
  );
};
